import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface UserRecord {
  id: string;
  name: string;
  initials: string;
  employeeId: string;
  email: string;
  role: string;
  status: 'Active' | 'Disabled';
  avatarColor: string;
  profileImage?: string;
}

// Helper to generate initials
const getInitials = (name: string) => {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Simple avatar color cycler based on index or ID sum
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-600',
  'bg-orange-100 text-orange-600',
  'bg-purple-100 text-purple-700',
  'bg-emerald-100 text-emerald-600',
  'bg-pink-100 text-pink-600',
  'bg-indigo-100 text-indigo-600',
];

const getAvatarColor = (idString: string) => {
  let hash = 0;
  for (let i = 0; i < idString.length; i++) {
    hash += idString.charCodeAt(i);
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatUsageMinutes = (minutes: number | null) => {
  if (minutes === null) return '-';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
};

const fmtSeconds = (seconds: number | null) => {
  if (seconds === null) return '-';
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}h ${rem}m`;
};

const UserTableRow: React.FC<{ user: UserRecord; navigate: any; loginData: any; onStatusChange: () => void }> = ({ user, navigate, loginData, onStatusChange }) => {
  const [usageMinutes, setUsageMinutes] = useState<number | null>(null);
  const [appActiveSeconds, setAppActiveSeconds] = useState<number | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/users/usage/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsageMinutes(data.total_minutes);
        setAppActiveSeconds(data.total_app_active_seconds ?? null);
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
    }
  };

  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, [user.id]);

  const handleForceLogout = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent row click navigation
    setShowForceLogoutModal(true);
  };

  const confirmForceLogout = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoggingOut(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/users/force-logout/${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setShowForceLogoutModal(false);
        onStatusChange(); // Refresh global status after logout
      } else {
        alert(data.message || 'Failed to force logout user');
      }
    } catch (err) {
      console.error('Error forcing logout:', err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <tr
      className="group transition-colors hover:bg-blue-50/50 cursor-pointer"
      onClick={() => navigate(`/user-management/${user.id}`)}
    >
      <td className="px-6 py-4 align-middle">
        <div className="flex items-center gap-3">
          {user.profileImage ? (
            <img
              src={user.profileImage}
              alt={user.name}
              className="h-9 w-9 rounded-full object-cover shadow-sm ring-1 ring-slate-100"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = ''; // Clear src on error to fallback to initials if needed, though here we just render the div below normally
                target.style.display = 'none';
              }}
            />
          ) : (
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold ${user.avatarColor}`}>
              {user.initials}
            </div>
          )}
          {!user.profileImage && (
            <div className="hidden">This is a fallback helper for the alt logic if needed</div>
          )}
          <div className="font-medium text-slate-900 group-hover:text-[#136bce] group-hover:underline decoration-[#136bce]/30 underline-offset-2 transition-colors">
            {user.name}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 align-middle text-slate-700 font-mono text-xs">{user.employeeId}</td>
      <td className="px-6 py-4 align-middle text-slate-700">{user.email}</td>
      <td className="px-6 py-4 align-middle">
        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${user.role === 'Admin' ? 'bg-purple-50 text-purple-700 ring-purple-600/20' : 'bg-slate-100 text-slate-600 ring-slate-500/10'}`}>
          {user.role}
        </span>
      </td>
      <td className="px-6 py-4 align-middle">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${user.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-slate-100 text-slate-600 ring-slate-500/20'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${user.status === 'Active' ? 'bg-emerald-600' : 'bg-slate-400'}`}></span>
          {user.status}
        </span>
      </td>
      <td className="px-6 py-4 align-middle">
        {loginData === null ? (
          <span className="text-slate-400 text-xs italic">Loading...</span>
        ) : loginData.isLoggedIn ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse"></span>
              Logged In
            </span>
            <button
              onClick={handleForceLogout}
              disabled={isLoggingOut}
              className="inline-flex items-center justify-center p-1 text-orange-500 hover:bg-orange-50 hover:text-orange-700 rounded-md transition-colors disabled:opacity-50"
              title="Force Logout"
            >
              {isLoggingOut ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent"></div>
              ) : (
                <span className="material-symbols-outlined text-[16px]">logout</span>
              )}
            </button>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-500/20">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
            Logged Out
          </span>
        )}
      </td>
      <td className="px-6 py-4 align-middle text-slate-600 font-medium text-xs">
        {formatUsageMinutes(usageMinutes)}
      </td>
      <td className="px-6 py-4 align-middle text-slate-600 font-medium text-xs">
        {fmtSeconds(appActiveSeconds)}
      </td>
      {/* Individual Force Logout Modal */}
      {showForceLogoutModal && (
        <td colSpan={0} className="p-0 border-0 h-0">
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm cursor-default" onClick={(e) => { e.stopPropagation(); setShowForceLogoutModal(false); }}>
            <div
              className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-orange-600 ring-4 ring-orange-50/50">
                  <span className="material-symbols-outlined text-[32px]">logout</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Force Logout User</h3>
                <p className="text-sm text-slate-500 mb-8">
                  Are you sure you want to force logout <span className="font-semibold text-slate-700">{user.name}</span>? They will be immediately disconnected.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowForceLogoutModal(false); }}
                    disabled={isLoggingOut}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmForceLogout}
                    disabled={isLoggingOut}
                    className="flex-1 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 sm:w-auto"
                  >
                    {isLoggingOut ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Processing...
                      </div>
                    ) : (
                      "Confirm Logout"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </td>
      )}
    </tr>
  );
};

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAdminOnly, setShowAdminOnly] = useState(false);

  // Bulk Delete States
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteCountdown, setBulkDeleteCountdown] = useState(0);

  // Force Logout States
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutType, setForceLogoutType] = useState('all');
  const [isForceLogoutDropdownOpen, setIsForceLogoutDropdownOpen] = useState(false);
  const forceLogoutDropdownRef = useRef<HTMLDivElement>(null);
  const [isForceLoggingOut, setIsForceLoggingOut] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Delete Inspections States
  const [showDeleteInspectionsModal, setShowDeleteInspectionsModal] = useState(false);
  const [deleteInspectionsUserId, setDeleteInspectionsUserId] = useState('');
  const [deleteInspectionsType, setDeleteInspectionsType] = useState('all');
  const [isDelUserDropdownOpen, setIsDelUserDropdownOpen] = useState(false);
  const [isDelTypeDropdownOpen, setIsDelTypeDropdownOpen] = useState(false);
  const delUserDropdownRef = useRef<HTMLDivElement>(null);
  const delTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [isDeletingInspections, setIsDeletingInspections] = useState(false);
  const [deleteInspectionsResult, setDeleteInspectionsResult] = useState<{ container: { deleted: number }; pilot: { deleted: number }; fabric: { deleted: number }; line: { deleted: number }; final: { deleted: number } } | null>(null);
  const [inspectionStats, setInspectionStats] = useState<{ total: number; by_type: { container: number; pilot: number; fabric: number; line: number; final: number }; last_active: string | null } | null>(null);
  const [isFetchingStats, setIsFetchingStats] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 10;

  const [globalLoginStatus, setGlobalLoginStatus] = useState<Record<string, any> | null>(null);

  const fetchGlobalLoginStatus = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/users/status-all`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const data = await response.json();
      if (data.success) {
        setGlobalLoginStatus(data.data);
      } else {
        setGlobalLoginStatus({});
      }
    } catch (err) {
      console.error('Error fetching global login status:', err);
      setGlobalLoginStatus({});
    }
  };

  useEffect(() => {
    fetchGlobalLoginStatus();

    // Poll every 5 seconds for real-time status updates without full page reload
    const interval = setInterval(() => {
      fetchGlobalLoginStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [refreshKey]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (delUserDropdownRef.current && !delUserDropdownRef.current.contains(e.target as Node)) {
        setIsDelUserDropdownOpen(false);
      }
      if (delTypeDropdownRef.current && !delTypeDropdownRef.current.contains(e.target as Node)) {
        setIsDelTypeDropdownOpen(false);
      }
      if (forceLogoutDropdownRef.current && !forceLogoutDropdownRef.current.contains(e.target as Node)) {
        setIsForceLogoutDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/v1/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch users');
      }

      const formattedUsers: UserRecord[] = data.data
        .map((u: any) => ({
          id: u.id,
          name: u.name || 'Unknown User',
          initials: getInitials(u.name || 'U'),
          employeeId: u.employee_id || 'N/A',
          email: u.email,
          role: u.role === 'admin' ? 'Admin' : 'QA Inspector',
          status: u.is_active ? 'Active' : 'Disabled',
          avatarColor: getAvatarColor(u.id),
          profileImage: u.profileImage,
        }));

      setUsers(formattedUsers);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching users.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  // Reset pagination on search or filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showAdminOnly]);

  // Bulk Delete Countdown Logic
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isBulkDeleteModalOpen && bulkDeleteCountdown > 0) {
      timer = setInterval(() => {
        setBulkDeleteCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isBulkDeleteModalOpen, bulkDeleteCountdown]);

  const startBulkDelete = () => {
    setBulkDeleteCountdown(5);
    setIsBulkDeleteModalOpen(true);
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/v1/users/999`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete users');
      }

      setIsBulkDeleteModalOpen(false);
      await fetchUsers(); // Refresh the list
    } catch (err: any) {
      alert(err.message || 'An error occurred during bulk deletion.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const ORPHANED_KEY = '__orphaned__';

  const fetchInspectionStats = async (userId: string) => {
    if (!userId) { setInspectionStats(null); return; }
    setIsFetchingStats(true);
    setInspectionStats(null);
    try {
      const url = userId === ORPHANED_KEY
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/users/orphaned/inspection-stats`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/users/${userId}/inspection-stats`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      if (data.success) setInspectionStats(data.data);
    } catch (err) {
      console.error('Failed to fetch inspection stats:', err);
    } finally {
      setIsFetchingStats(false);
    }
  };

  const handleDeleteInspections = async () => {
    if (!deleteInspectionsUserId) return;
    setIsDeletingInspections(true);
    setDeleteInspectionsResult(null);
    try {
      const url = deleteInspectionsUserId === ORPHANED_KEY
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/users/orphaned/inspections?type=${deleteInspectionsType}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/users/${deleteInspectionsUserId}/inspections?type=${deleteInspectionsType}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to delete inspections.');
      setDeleteInspectionsResult(data.data);
    } catch (err: any) {
      alert(err.message || 'An error occurred while deleting inspections.');
    } finally {
      setIsDeletingInspections(false);
    }
  };

  const handleForceLogoutAll = async () => {
    setIsForceLoggingOut(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/users/force-logout-all?type=${forceLogoutType}`, {
        method: 'GET', // The API is defined as a GET request
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to force logout users.');
      }

      setShowForceLogoutModal(false);
      setRefreshKey(prev => prev + 1);

      // If Admin or All Users are selected, logout the current user as well
      if (forceLogoutType === 'all' || forceLogoutType === 'admin') {
        logout();
        return;
      }

      fetchUsers();
    } catch (err: any) {
      alert(err.message || 'An error occurred during force logout.');
    } finally {
      setIsForceLoggingOut(false);
    }
  };

  // Filter users by search term and admin status
  const filteredUsers = users.filter((user) => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    if (showAdminOnly) {
      return matchesSearch && user.role === 'Admin';
    }
    return matchesSearch;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
        <p className="text-sm text-slate-500">Manage user accounts, roles, and system access permissions</p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between md:flex-nowrap">
        <div className="relative w-full md:max-w-md">
          <input
            className="w-full rounded-lg border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
            placeholder="Search by Name, Employee ID or Email..."
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
        </div>
        <div className="flex gap-3 w-full md:w-auto items-center flex-shrink-0">
          <div className="flex items-center gap-3 mr-4 pr-4 border-r border-slate-200 flex-shrink-0">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Show Admin Users Only</span>
            <button
              onClick={() => setShowAdminOnly(!showAdminOnly)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showAdminOnly ? 'bg-[#136bce]' : 'bg-slate-200'}`}
              type="button"
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showAdminOnly ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </div>
          <button
            onClick={() => setShowForceLogoutModal(true)}
            className="flex items-center justify-center gap-2 rounded-lg border border-orange-200 bg-white px-5 py-2.5 text-sm font-medium text-orange-600 shadow-sm transition-all hover:bg-orange-50 hover:border-orange-300 whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-[20px]">logout</span>
            Force Logout Users
          </button>
          <button
            onClick={() => { setDeleteInspectionsUserId(''); setDeleteInspectionsType('all'); setDeleteInspectionsResult(null); setInspectionStats(null); setShowDeleteInspectionsModal(true); }}
            className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-5 py-2.5 text-sm font-medium text-red-600 shadow-sm transition-all hover:bg-red-50 hover:border-red-300 whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-[20px]">folder_delete</span>
            Delete Inspections
          </button>
          <button
            onClick={startBulkDelete}
            className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-5 py-2.5 text-sm font-medium text-red-600 shadow-sm transition-all hover:bg-red-50 hover:border-red-300 whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-[20px]">delete_sweep</span>
            Delete all users
          </button>
          <button
            onClick={() => navigate('/user-management/add')}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-medium text-white shadow-sm shadow-blue-500/30 transition-all hover:bg-blue-700 whitespace-nowrap flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            Add New User
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4 border-b border-slate-200">Full Name</th>
                <th className="px-6 py-4 border-b border-slate-200">Employee ID</th>
                <th className="px-6 py-4 border-b border-slate-200">Email ID</th>
                <th className="px-6 py-4 border-b border-slate-200">User Types</th>
                <th className="px-6 py-4 border-b border-slate-200">Status</th>
                <th className="px-6 py-4 border-b border-slate-200">Login Status</th>
                <th className="px-6 py-4 border-b border-slate-200">Account Usage</th>
                <th className="px-6 py-4 border-b border-slate-200">App Usage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                      <p>Loading users...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                    No users found matching your search.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <UserTableRow
                    key={user.id}
                    user={user}
                    navigate={navigate}
                    loginData={globalLoginStatus === null ? null : (globalLoginStatus[user.id] || { isLoggedIn: false })}
                    onStatusChange={() => setRefreshKey(prev => prev + 1)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium">{filteredUsers.length > 0 ? startIndex + 1 : 0}</span> to{' '}
            <span className="font-medium">{Math.min(startIndex + USERS_PER_PAGE, filteredUsers.length)}</span> of{' '}
            <span className="font-medium">{filteredUsers.length}</span> results
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-[#136bce] text-white shadow-sm ring-1 ring-[#136bce]'
                    : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => !isBulkDeleting && setIsBulkDeleteModalOpen(false)}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">warning</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete All Users?</h3>
              <p className="text-sm text-slate-500 mb-8">
                Are you sure you want to delete <span className="font-semibold text-slate-700">all non-admin users</span>? This action is permanent, deletes all associated data, and cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsBulkDeleteModalOpen(false)}
                  disabled={isBulkDeleting}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting || bulkDeleteCountdown > 0}
                  className="flex-3 rounded-lg bg-red-600 px-6 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-md shadow-red-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  type="button"
                  style={{ flexGrow: 2 }}
                >
                  {isBulkDeleting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      Confirm Delete {bulkDeleteCountdown > 0 ? `(${bulkDeleteCountdown}s)` : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Inspections Modal */}
      {showDeleteInspectionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div
            className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Critical Warning Banner */}
            <div className="flex items-center gap-3 bg-red-600 px-6 py-3">
              <span className="material-symbols-outlined text-white text-[20px]">error</span>
              <p className="text-sm font-semibold text-white">Critical Action — This cannot be undone</p>
            </div>

            <div className="p-8">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                  <span className="material-symbols-outlined text-[32px]">folder_delete</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Delete User Inspections</h3>
                <p className="text-sm text-slate-500">
                  Permanently deletes all selected inspection records and their associated files from storage.
                </p>
              </div>

              {deleteInspectionsResult ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 mb-6 text-center">
                  <span className="material-symbols-outlined text-emerald-600 text-[28px] mb-1 block">check_circle</span>
                  <p className="text-sm font-semibold text-emerald-800 mb-2">Deleted Successfully</p>
                   <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-emerald-700">
                    <span><span className="font-bold">{deleteInspectionsResult.container.deleted}</span> container inspection(s)</span>
                    <span><span className="font-bold">{deleteInspectionsResult.pilot.deleted}</span> pilot inspection(s)</span>
                    <span><span className="font-bold">{deleteInspectionsResult.fabric.deleted}</span> fabric inspection(s)</span>
                    <span><span className="font-bold">{deleteInspectionsResult.line.deleted}</span> line inspection(s)</span>
                    <span><span className="font-bold">{deleteInspectionsResult.final.deleted}</span> final inspection(s)</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mb-8">
                  {/* User Selector */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Select User</label>
                    <div className="relative" ref={delUserDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsDelUserDropdownOpen((prev: boolean) => !prev)}
                        className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer bg-white text-left"
                      >
                        {!deleteInspectionsUserId ? (
                          <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[14px] text-slate-400">person</span>
                          </span>
                        ) : deleteInspectionsUserId === ORPHANED_KEY ? (
                          <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[14px] text-slate-500">person_off</span>
                          </span>
                        ) : (() => {
                          const sel = users.find(u => u.id === deleteInspectionsUserId);
                          return sel?.profileImage ? (
                            <img src={sel.profileImage} alt={sel.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                          ) : (
                            <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${sel?.avatarColor || 'bg-slate-200 text-slate-600'}`}>
                              {sel?.initials || '?'}
                            </span>
                          );
                        })()}
                        <span className="truncate flex-1 text-slate-700">
                          {!deleteInspectionsUserId
                            ? '— Choose a user —'
                            : deleteInspectionsUserId === ORPHANED_KEY
                            ? 'Deleted Users (orphaned)'
                            : (() => { const sel = users.find(u => u.id === deleteInspectionsUserId); return sel ? sel.name : '—'; })()}
                        </span>
                        <span className="material-symbols-outlined pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                      </button>
                      {isDelUserDropdownOpen && (
                        <div className="absolute left-0 top-full mt-1 z-30 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-56 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => { setDeleteInspectionsUserId(''); fetchInspectionStats(''); setIsDelUserDropdownOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${deleteInspectionsUserId === '' ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-500'}`}
                          >
                            <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-[14px] text-slate-400">person</span>
                            </span>
                            — Choose a user —
                          </button>
                          {users.filter(u => u.role !== 'Admin').map((u) => (
                            <button
                              type="button"
                              key={u.id}
                              onClick={() => { setDeleteInspectionsUserId(u.id); fetchInspectionStats(u.id); setIsDelUserDropdownOpen(false); }}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${deleteInspectionsUserId === u.id ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                            >
                              {u.profileImage ? (
                                <img src={u.profileImage} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                              ) : (
                                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u.avatarColor}`}>
                                  {u.initials}
                                </span>
                              )}
                              <span className="truncate flex-1">{u.name}</span>
                              <span className="text-xs text-slate-400 shrink-0 hidden sm:block truncate max-w-[120px]">{u.email}</span>
                            </button>
                          ))}
                          <div className="mx-3 my-1 h-px bg-slate-100" />
                          <button
                            type="button"
                            onClick={() => { setDeleteInspectionsUserId(ORPHANED_KEY); fetchInspectionStats(ORPHANED_KEY); setIsDelUserDropdownOpen(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-red-50 transition-colors text-left ${deleteInspectionsUserId === ORPHANED_KEY ? 'bg-red-50 text-red-600 font-semibold' : 'text-slate-600'}`}
                          >
                            <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <span className="material-symbols-outlined text-[14px] text-slate-500">person_off</span>
                            </span>
                            Deleted Users (orphaned inspections)
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Selected user stats card */}
                    {deleteInspectionsUserId && (() => {
                      const isOrphaned = deleteInspectionsUserId === ORPHANED_KEY;
                      const sel = isOrphaned ? null : users.find(u => u.id === deleteInspectionsUserId);
                      if (!isOrphaned && !sel) return null;
                      const formatRelativeTime = (iso: string | null) => {
                        if (!iso) return 'Never';
                        const diff = Date.now() - new Date(iso).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'Just now';
                        if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
                        const days = Math.floor(hrs / 24);
                        return `${days} day${days !== 1 ? 's' : ''} ago`;
                      };
                      return (
                        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                          {/* User identity row */}
                          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                            {isOrphaned ? (
                              <div className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ring-2 ring-white shadow-sm bg-slate-100 text-slate-500">
                                <span className="material-symbols-outlined text-[20px]">person_off</span>
                              </div>
                            ) : sel?.profileImage ? (
                              <img src={sel.profileImage} alt={sel.name} className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0" />
                            ) : (
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ring-2 ring-white shadow-sm ${sel?.avatarColor}`}>
                                {sel?.initials}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              {isOrphaned ? (
                                <>
                                  <p className="text-sm font-semibold text-slate-800">Deleted Users</p>
                                  <p className="text-xs text-slate-500">Inspections with no linked user account</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm font-semibold text-slate-800 truncate">{sel?.name}</p>
                                  <p className="text-xs text-slate-500 truncate">{sel?.email}</p>
                                </>
                              )}
                            </div>
                            {!isOrphaned && (
                              <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
                                <span className="material-symbols-outlined text-[14px]">schedule</span>
                                {isFetchingStats ? (
                                  <span>Loading...</span>
                                ) : (
                                  <span>{formatRelativeTime(inspectionStats?.last_active ?? null)}</span>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Stats row */}
                          {isFetchingStats ? (
                            <div className="flex items-center justify-center gap-2 py-4 text-xs text-slate-400">
                              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500"></div>
                              Fetching inspection data...
                            </div>
                          ) : inspectionStats ? (
                            <div className="grid grid-cols-6 divide-x divide-slate-200">
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-slate-800">{inspectionStats.total}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Total</span>
                              </div>
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-blue-600">{inspectionStats.by_type.container}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Container</span>
                              </div>
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-purple-600">{inspectionStats.by_type.pilot}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Pilot</span>
                              </div>
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-emerald-600">{inspectionStats.by_type.fabric}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Fabric</span>
                              </div>
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-orange-600">{inspectionStats.by_type.line || 0}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Line</span>
                              </div>
                              <div className="flex flex-col items-center py-3 gap-0.5">
                                <span className="text-lg font-bold text-rose-600">{inspectionStats.by_type.final || 0}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Final</span>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Inspection Type Selector */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Inspection Type to Delete</label>
                    <div className="relative" ref={delTypeDropdownRef}>
                      {(() => {
                          const typeOptions = [
                            { value: 'all', label: 'All Inspections', icon: 'fact_check', color: 'text-slate-600', bg: 'bg-slate-100' },
                            { value: 'pilot', label: 'Pilot Inspections Only', icon: 'rocket_launch', color: 'text-purple-600', bg: 'bg-purple-100' },
                            { value: 'container', label: 'Container Inspections Only', icon: 'local_shipping', color: 'text-blue-600', bg: 'bg-blue-100' },
                            { value: 'fabric', label: 'Fabric Inspections Only', icon: 'texture', iconMaterial: true, color: 'text-emerald-600', bg: 'bg-emerald-100' },
                            { value: 'line', label: 'Line Inspections Only', icon: 'precision_manufacturing', color: 'text-orange-600', bg: 'bg-orange-100' },
                            { value: 'final', label: 'Final Inspections Only', icon: 'inventory_2', color: 'text-rose-600', bg: 'bg-rose-100' },
                          ];
                        const selected = typeOptions.find(o => o.value === deleteInspectionsType) || typeOptions[0];
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() => setIsDelTypeDropdownOpen((prev: boolean) => !prev)}
                              className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer bg-white text-left"
                            >
                              <span className={`h-6 w-6 rounded-full ${selected.bg} flex items-center justify-center shrink-0`}>
                                <span className={`material-symbols-outlined text-[14px] ${selected.color}`}>{selected.icon}</span>
                              </span>
                              <span className="truncate flex-1">{selected.label}</span>
                              <span className="material-symbols-outlined pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                            </button>
                            {isDelTypeDropdownOpen && (
                              <div className="absolute left-0 bottom-full mb-1 z-30 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1">
                                {typeOptions.map(opt => (
                                  <button
                                    type="button"
                                    key={opt.value}
                                    onClick={() => { setDeleteInspectionsType(opt.value); setIsDelTypeDropdownOpen(false); }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${deleteInspectionsType === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                                  >
                                    <span className={`h-6 w-6 rounded-full ${opt.bg} flex items-center justify-center shrink-0`}>
                                      <span className={`material-symbols-outlined text-[14px] ${opt.color}`}>{opt.icon}</span>
                                    </span>
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => { setShowDeleteInspectionsModal(false); setDeleteInspectionsResult(null); }}
                  disabled={isDeletingInspections}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                >
                  {deleteInspectionsResult ? 'Close' : 'Cancel'}
                </button>
                {!deleteInspectionsResult && (
                  <button
                    type="button"
                    onClick={handleDeleteInspections}
                    disabled={isDeletingInspections || !deleteInspectionsUserId}
                    className="flex-1 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeletingInspections ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Deleting...
                      </div>
                    ) : (
                      'Confirm Delete'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Logout Confirmation Modal */}
      {showForceLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-orange-600 ring-4 ring-orange-50/50">
                <span className="material-symbols-outlined text-[32px]">logout</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Force Logout Users</h3>
              <p className="text-sm text-slate-500 mb-6">
                Select which group of users you want to forcefully log out from all their active sessions.
              </p>

              <div className="text-left mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">Target Group</label>
                {(() => {
                  const logoutTypeOptions = [
                    { value: 'all', label: 'All Users', icon: 'group', color: 'text-slate-600', bg: 'bg-slate-100' },
                    { value: 'admin', label: 'Admin Users', icon: 'admin_panel_settings', color: 'text-red-600', bg: 'bg-red-100' },
                    { value: 'user', label: 'Non-Admin Users', icon: 'person', color: 'text-blue-600', bg: 'bg-blue-100' },
                  ];
                  const selected = logoutTypeOptions.find(o => o.value === forceLogoutType) || logoutTypeOptions[0];
                  return (
                    <div className="relative" ref={forceLogoutDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsForceLogoutDropdownOpen((prev: boolean) => !prev)}
                        className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer bg-white text-left"
                      >
                        <span className={`h-6 w-6 rounded-full ${selected.bg} flex items-center justify-center shrink-0`}>
                          <span className={`material-symbols-outlined text-[14px] ${selected.color}`}>{selected.icon}</span>
                        </span>
                        <span className="truncate">{selected.label}</span>
                        <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                      </button>
                      {isForceLogoutDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 z-30 w-full rounded-xl border border-slate-200 bg-white shadow-lg py-1">
                          {logoutTypeOptions.map(opt => (
                            <button
                              type="button"
                              key={opt.value}
                              onClick={() => { setForceLogoutType(opt.value); setIsForceLogoutDropdownOpen(false); }}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${forceLogoutType === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                            >
                              <span className={`h-6 w-6 rounded-full ${opt.bg} flex items-center justify-center shrink-0`}>
                                <span className={`material-symbols-outlined text-[14px] ${opt.color}`}>{opt.icon}</span>
                              </span>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowForceLogoutModal(false)}
                  disabled={isForceLoggingOut}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleForceLogoutAll}
                  disabled={isForceLoggingOut}
                  className="flex-1 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 sm:w-auto"
                >
                  {isForceLoggingOut ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Processing...
                    </div>
                  ) : (
                    "Confirm Logout"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
